/* eslint-disable react/no-this-in-sfc */
import { useMutation } from "@tanstack/react-query";
import GKCharts from "components/GKCharts";
import Highcharts from "highcharts/highstock";
import { useEffect, useMemo, useState } from "react";
import { moneyCommaSeparator } from "utils";
import axiosInstance from "utils/axiosInstance";
import { formatYAxisLabel } from "utils/helpers";

interface SelectedAsset {
  gcode: string;
  name: string;
}

interface PerformanceChatProps {
  portfolios: number[];
  selectedPortfolios: any;
  assets: SelectedAsset[];
}

const PerformanceChat = ({
  portfolios,
  selectedPortfolios,
  assets,
}: PerformanceChatProps) => {
  const DEFAULT_BENCHMARK = "014809908bef61737dd49eb9";

  const generateColor = (index: number) =>
    `hsl(${(index * 47) % 360}, 65%, 50%)`;

  const [navData, setNavData] = useState<any>(null);

  /** -------------------- CLEAN + SANITIZE VALUES -------------------- */
  const sanitizeValue = (val: any, isBenchmark = false) => {
    const num = Number(val);
    if (Number.isNaN(num) || num <= 0) return null;
    if (!isBenchmark && num > 1e7) return null; // NAV never beyond 1 Cr for non-benchmark data
    return num;
  };

  const convertPortfolioData = (arr: any[]) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => {
        const y = sanitizeValue(item.nav);
        if (!y) return null;
        return [new Date(item.date).getTime(), y];
      })
      .filter(Boolean);
  };

  const convertBenchmarkData = (arr: any[]) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => {
        const y = sanitizeValue(item.nav, true); // Pass true to indicate this is benchmark data
        if (y === null) return null;
        return [new Date(item.date).getTime(), y];
      })
      .filter(Boolean);
  };

  const convertAssetData = (arr: any[]) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => {
        const y = sanitizeValue(item.nav);
        if (!y) return null;
        return [new Date(item.date).getTime(), y];
      })
      .filter(Boolean);
  };

  /** -------------------- Fetch NAV API -------------------- */
  const { mutate: fetchNAV, isLoading } = useMutation({
    mutationKey: ["PERFORMANCE_NAV"],
    mutationFn: () =>
      axiosInstance.post("/holdings/nav/", {
        benchmark: DEFAULT_BENCHMARK,
        portfolios,
        assets: assets.length ? assets.map((a) => a.gcode) : [],
      }),
    onSuccess: (res) => setNavData(res.data),
  });

  useEffect(() => {
    if (portfolios.length) {
      fetchNAV();
    }
  }, [portfolios, assets]);

  /** -------------------- HIGHCHART OPTIONS -------------------- */
  const chartDataOption = useMemo(() => {
    return {
      chart: {
        borderRadius: 5,
        backgroundColor: "var(--white)",
        height: 550,
        spacing: [10, 10, 10, 10],
        marginRight: 50,
      },

      title: {
        text: "",
      },

      tooltip: {
        shared: true,
        split: false,
        headerFormat: "",
        // eslint-disable-next-line func-names
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          let content = "";

          (this.points || []).forEach((point) => {
            content += `
        <span style="color:${point.color}">\u25CF</span>
        ${point.series.name}:
        <b>${moneyCommaSeparator(point.y)}</b><br/>
      `;
          });

          return content;
        },
        valueDecimals: 2,
      },

      legend: {
        enabled: true,
        layout: "horizontal",
        align: "center",
        verticalAlign: "top",
        itemStyle: {
          color: "var(--black)",
          fontWeight: "bold",
        },
      },

      rangeSelector: {
        inputStyle: { color: "var(--primary-900)" },
        labelStyle: { color: "var(--primary-900)" },
        buttonTheme: {
          width: 50,
          fill: "#fff",
          style: {
            background: "var(--white)",
            color: "var(--neutral-500)",
            textTransform: "uppercase",
          },
          states: {
            select: {
              fill: "#F8FAFC",
              style: { color: "var(--neutral-500)" },
            },
          },
        },
      },

      plotOptions: {
        series: {
          animation: false,
          states: { hover: { enabled: false } },
        },
      },

      xAxis: {
        type: "date",
        labels: {
          style: {
            color: "var(--primary-900)",
            fontSize: "12px",
            fontWeight: "bold",
          },
          formatter(this: any) {
            return Highcharts.dateFormat("%b %e, %Y", this.value);
          },
        },
      },

      yAxis: {
        opposite: false,
        gridLineColor: "var(--neutral-100)",
        min: 0,
        labels: {
          style: {
            color: "var(--primary-900)",
            fontSize: "12px",
            fontWeight: "bold",
          },
          formatter(this: any) {
            return formatYAxisLabel(this.value);
          },
        },
      },

      /** -------------------- SERIES -------------------- */
      series: [
        {
          name: "Nifty 50",
          data: convertBenchmarkData(navData?.data?.[DEFAULT_BENCHMARK] || []),
          color: "#007AFF",
        },

        // ---------------- Portfolio Series ----------------
        ...portfolios.map((portfolioId, index) => {
          const portfolioObj = selectedPortfolios?.find(
            (p: any) => Number(p.id) === Number(portfolioId),
          );

          return {
            name: portfolioObj?.name || `Portfolio ${portfolioId}`,
            data: convertPortfolioData(navData?.data?.[portfolioId] || []),
            color: generateColor(index),
          };
        }),

        // ---------------- Asset Series ----------------
        ...assets.map((asset, index) => {
          return {
            name: asset.name,
            data: convertAssetData(navData?.data?.[asset.gcode] || []),
            dashStyle: "ShortDot",
            color: generateColor(index + portfolios.length),
          };
        }),
      ],
    };
  }, [navData]);

  return (
    <GKCharts
      chartOptions={chartDataOption}
      rangeSelector
      loading={isLoading}
      navigator
    />
  );
};

export default PerformanceChat;
